import { ReactNode } from "react";
import { CounterInputV2, DropdownV2, FormCardV2, FormGridV2, InputV2, OptionV2 } from "../FormControlsV2";

export type ProductFormFieldV2 =
  | "trade_name"
  | "manufacturer"
  | "classification"
  | "sub_classification"
  | "expected_consumption"
  | "supply_frequency";

export type ProductFormValueV2 = {
  id?: string | number;
  trade_name: string;
  manufacturer: string;
  classification: OptionV2 | null;
  sub_classification: OptionV2 | null;
  expected_consumption: number;
  supply_frequency: number;
};

export type ProductFormErrorsV2 = Partial<Record<ProductFormFieldV2, string>>;

type ProductFormV2Props = {
  value: ProductFormValueV2;
  errors?: ProductFormErrorsV2;
  classifications: OptionV2[];
  subClassificationLabel?: string;
  sideSlot?: ReactNode;
  disabled?: boolean;
  onChange: (value: ProductFormValueV2) => void;
};

export function createEmptyProductFormValue(): ProductFormValueV2 {
  return {
    trade_name: "",
    manufacturer: "",
    classification: null,
    sub_classification: null,
    expected_consumption: 0,
    supply_frequency: 0,
  };
}

export function ProductFormV2({
  value,
  errors = {},
  classifications,
  subClassificationLabel = "Sub classificação",
  sideSlot,
  disabled = false,
  onChange,
}: ProductFormV2Props) {
  const subClassificationOptions = Array.isArray(value.classification?.info) ? value.classification?.info : [];

  function update(partial: Partial<ProductFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  return (
    <FormCardV2>
      <div className={sideSlot ? "create-product-v2__card-layout" : ""}>
      <div className="create-product-v2__fields">
        <FormGridV2>
          <InputV2
            name="trade_name"
            label="Nome comercial do produto"
            value={value.trade_name}
            disabled={disabled}
            error={errors.trade_name}
            onChange={(trade_name) => update({ trade_name })}
          />
          <InputV2
            name="manufacturer"
            label="Fabricante"
            value={value.manufacturer}
            disabled={disabled}
            error={errors.manufacturer}
            onChange={(manufacturer) => update({ manufacturer })}
          />
          <DropdownV2
            name="classification"
            label="Classificação"
            value={value.classification}
            options={classifications}
            searchable
            disabled={disabled}
            error={errors.classification}
            onChange={(classification) =>
              update({
                classification,
                sub_classification: null,
                expected_consumption: 0,
                supply_frequency: 0,
              })
            }
          />
          {value.classification && (
            <DropdownV2
              name="sub_classification"
              label={subClassificationLabel}
              value={value.sub_classification}
              options={subClassificationOptions}
              searchable
              disabled={disabled}
              error={errors.sub_classification}
              onChange={(sub_classification) =>
                update({
                  sub_classification,
                  expected_consumption: 0,
                  supply_frequency: 0,
                })
              }
            />
          )}
          {value.sub_classification && (
            <CounterInputV2
              name="expected_consumption"
              label="Consumo esperado"
              value={value.expected_consumption}
              max={10000000}
              suffix="g/kg de PV"
              disabled={disabled}
              error={errors.expected_consumption}
              onChange={(expected_consumption) => update({ expected_consumption, supply_frequency: 0 })}
            />
          )}
          {value.expected_consumption > 0 && (
            <CounterInputV2
              name="supply_frequency"
              label="Frequência de fornecimento"
              value={value.supply_frequency}
              max={7}
              suffix="x por semana"
              disabled={disabled}
              error={errors.supply_frequency}
              onChange={(supply_frequency) => update({ supply_frequency })}
            />
          )}
        </FormGridV2>
      </div>
        {sideSlot && <div className="create-product-v2__side-slot">{sideSlot}</div>}
      </div>
    </FormCardV2>
  );
}
